import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, finalize, startWith } from 'rxjs';
import { TaskService } from '../../services/task.service';
import { FixedEntityField, JsonToFixedTaskConfig } from '../../models/task-config.model';
import { FileTransferComponent } from '../file-transfer/file-transfer.component';
import { ToastService } from '../../services/toast.service';
import { computeFixedFieldTotals } from '../../utils/sum-fixed-field-lengths';
import { ViewSchemaEditorComponent } from '../view-schema-editor/view-schema-editor.component';

@Component({
  selector: 'app-json-to-fixed-task-config',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FileTransferComponent, ViewSchemaEditorComponent],
  templateUrl: './json-to-fixed-task-config.component.html',
  styleUrl: './json-to-fixed-task-config.component.scss',
})
export class JsonToFixedTaskConfigComponent implements OnInit {
  private fb = inject(FormBuilder);
  private taskService = inject(TaskService);
  private http = inject(HttpClient);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private fieldIdCounter = 0;

  isExecuting = signal(false);
  isSaving = signal(false);
  isLoading = signal(false);
  /** 入力JSONからキーをエンティティへ反映する処理中 */
  reflectingKeys = signal(false);
  isLoadingResult = signal(false);
  hasExecuted = signal(false);
  error = signal<string | null>(null);
  /** 固定長テキスト（JSON ではないため生文字列で保持） */
  resultText = signal<string>('');
  /** 各フィールド length の合計（レイアウト確認用） */
  totalFieldLength = signal(0);
  /** 各フィールド count（1 以上）の合計 */
  totalFieldCountSum = signal(0);

  private readonly BUCKET_CODE = 'x93jkd8f-qpz1-mv8w';

  configForm: FormGroup = this.fb.group({
    wbsTaskId: ['convert-demo-json-to-fixed', Validators.required],
    id: ['convert-demo-json-to-fixed', Validators.required],
    name: ['JSON→固定長コンバータ（デモ）', Validators.required],
    searchPath: ['seino/input/sales_array.json', Validators.required],
    /** キー反映時のみ使用（保存ペイロードには含めない） */
    baseObjectPath: [''],
    savePath: ['seino/output/sales_fixed.txt', Validators.required],
    entityFields: this.fb.array([]),
    viewSchemaJson: [''],
    crossJoinOptionsJson: [''],
    saveModelJson: [''],
  });

  ngOnInit(): void {
    this.entityFields.valueChanges
      .pipe(startWith(this.entityFields.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const t = computeFixedFieldTotals(this.entityFields.getRawValue());
        this.totalFieldLength.set(t.lengthSum);
        this.totalFieldCountSum.set(t.countSum);
      });
    this.fetchSavedConfig();
  }

  get entityFields(): FormArray {
    return this.configForm.get('entityFields') as FormArray;
  }

  addField(field?: Partial<FixedEntityField>): void {
    const fieldGroup = this.fb.group({
      // 並び替え時に index track だとDOMが再利用されて表示が動かないため、安定IDを付与する
      _id: [(field as { _id?: number } | undefined)?._id ?? this.fieldIdCounter++],
      fieldName: [field?.fieldName || '', Validators.required],
      length: [field?.length || null, [Validators.required, Validators.min(1)]],
      trimType: [field?.trimType || ''],
      fieldType: [field?.fieldType || ''],
      count: [field?.count != null ? field.count : null],
      format: [field?.format ?? ''],
      textAlignment: [field?.textAlignment ?? ''],
    });
    this.entityFields.push(fieldGroup);
  }

  removeField(index: number): void {
    this.entityFields.removeAt(index);
  }

  moveFieldUp(index: number): void {
    if (index <= 0) return;
    const field = this.entityFields.at(index);
    this.entityFields.removeAt(index);
    this.entityFields.insert(index - 1, field);
  }

  moveFieldDown(index: number): void {
    if (index >= this.entityFields.length - 1) return;
    const field = this.entityFields.at(index);
    this.entityFields.removeAt(index);
    this.entityFields.insert(index + 1, field);
  }

  /**
   * 入力JSONの先頭レコードからキーを読み取り、未登録分をエンティティフィールドに追加する。
   * length / fieldType はサンプル値から推定（必要に応じて手で調整）。
   */
  reflectKeysFromJson(): void {
    const searchPath = String(this.configForm.get('searchPath')?.value ?? '').trim();
    if (!searchPath) {
      this.toastService.error('入力JSONファイルパスを指定してください');
      return;
    }

    const baseObjectPath = String(this.configForm.get('baseObjectPath')?.value ?? '').trim();
    this.reflectingKeys.set(true);

    const isUrl = /^https?:\/\//i.test(searchPath);
    const source$: Observable<unknown> = isUrl
      ? this.http.get<unknown>(searchPath)
      : (this.taskService.getFileContent(searchPath) as Observable<unknown>);

    source$.pipe(finalize(() => this.reflectingKeys.set(false))).subscribe({
      next: (data: unknown) => {
        let target: unknown = data;
        if (baseObjectPath) {
          for (const part of baseObjectPath.split('.').filter((p) => p.length > 0)) {
            if (target && typeof target === 'object' && target !== null && part in (target as object)) {
              target = (target as Record<string, unknown>)[part];
            } else {
              this.toastService.error(`基準パス内の「${part}」が見つかりません`);
              return;
            }
          }
        }

        let sample: Record<string, unknown> | null = null;
        if (Array.isArray(target)) {
          const first = target[0];
          if (first && typeof first === 'object' && !Array.isArray(first)) {
            sample = first as Record<string, unknown>;
          }
        } else if (typeof target === 'object' && target !== null) {
          sample = target as Record<string, unknown>;
        }

        if (!sample) {
          this.toastService.error('先頭のオブジェクトを取得できませんでした（配列の先頭がオブジェクトか確認してください）');
          return;
        }

        const keys = Object.keys(sample);
        if (keys.length === 0) {
          this.toastService.error('キーが存在しません');
          return;
        }

        const currentFields = this.entityFields.getRawValue() as { fieldName?: string }[];
        let added = 0;
        for (const key of keys) {
          if (currentFields.some((f) => f.fieldName === key)) continue;
          const v = sample[key];
          this.addField({
            fieldName: key,
            length: this.inferFixedLengthFromSample(v),
            fieldType: this.inferFieldTypeFromSample(v),
            trimType: 'rtrim',
          });
          added++;
          currentFields.push({ fieldName: key });
        }

        if (added === 0) {
          this.toastService.success(`キーは ${keys.length} 件すべて既に項目名として登録済みです`);
        } else {
          this.toastService.success(`${added} 件のキーを反映しました（検出 ${keys.length} キー）`);
        }
      },
      error: (err: unknown) => {
        console.error('reflectKeysFromJson', err);
        this.toastService.error('JSONの取得に失敗しました');
      },
    });
  }

  /** 先頭レコードの値から固定長の長さを推定（あくまで初期値） */
  private inferFixedLengthFromSample(value: unknown): number {
    if (value === null || value === undefined) return 20;
    if (typeof value === 'boolean') return 5;
    if (typeof value === 'number') {
      const len = String(value).length;
      return Math.min(Math.max(len, 4), 30);
    }
    if (typeof value === 'string') return Math.min(Math.max(value.length, 1), 255);
    if (typeof value === 'object') return 80;
    return 20;
  }

  private inferFieldTypeFromSample(value: unknown): string {
    if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'double';
    return 'string';
  }

  fetchSavedConfig(): void {
    const taskId = this.configForm.get('wbsTaskId')?.value;
    if (!taskId) return;

    this.isLoading.set(true);
    this.taskService.getTaskFile(taskId).subscribe({
      next: (res) => {
        // タスク種別により data 形状が異なるため既存固定長画面と同様に緩い型で読み取る
        const config = (res as { data?: unknown }).data as Record<string, unknown> | undefined;
        const converter = (config?.['profiles'] as { actions?: { converter?: Record<string, unknown> } } | undefined)
          ?.actions?.converter;

        if (config && converter) {
          const saveTarget = converter['saveTarget'] as { savePath?: string; saveModel?: object } | undefined;
          this.configForm.patchValue({
            wbsTaskId: taskId,
            id: String(config['id'] ?? ''),
            name: String(config['name'] ?? ''),
            searchPath: (converter['target'] as { searchPath?: string } | undefined)?.searchPath || '',
            savePath: saveTarget?.savePath || '',
            viewSchemaJson:
              converter['viewSchema'] && Object.keys(converter['viewSchema'] as object).length > 0
                ? JSON.stringify(converter['viewSchema'], null, 2)
                : '',
            crossJoinOptionsJson:
              converter['crossJoinOptions'] &&
              Object.keys(converter['crossJoinOptions'] as object).length > 0
                ? JSON.stringify(converter['crossJoinOptions'], null, 2)
                : '',
            saveModelJson:
              saveTarget?.saveModel && Object.keys(saveTarget.saveModel).length > 0
                ? JSON.stringify(saveTarget.saveModel, null, 2)
                : '',
          });

          this.entityFields.clear();
          const savedFields =
            (
              (converter['convertSchema'] as { fields?: FixedEntityField[] } | undefined) ??
              (converter['entitySchema'] as { fields?: FixedEntityField[] } | undefined)
            )?.fields || [];
          savedFields.forEach((field: FixedEntityField) => {
            this.addField(field);
          });
        }
        this.isLoading.set(false);
      },
      error: () => {
        if (this.entityFields.length === 0) {
          this.addField({ fieldName: 'productId', length: 40, fieldType: 'string' });
          this.addField({ fieldName: 'quantity', length: 10, fieldType: 'int' });
        }
        this.isLoading.set(false);
      },
    });
  }

  saveConfig(): void {
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    this.error.set(null);

    const formValue = this.configForm.getRawValue();

    let viewSchema: unknown = null;
    let crossJoinOptions: unknown = null;
    let saveModel: unknown = null;

    try {
      if (formValue.viewSchemaJson && formValue.viewSchemaJson.trim() !== '') {
        viewSchema = JSON.parse(formValue.viewSchemaJson);
      }
      if (formValue.crossJoinOptionsJson && formValue.crossJoinOptionsJson.trim() !== '') {
        crossJoinOptions = JSON.parse(formValue.crossJoinOptionsJson);
      }
      if (formValue.saveModelJson && formValue.saveModelJson.trim() !== '') {
        saveModel = JSON.parse(formValue.saveModelJson);
      }
    } catch {
      this.error.set('JSONの形式が不正です。View Schema, Cross Join Options, Save ModelのJSONを確認してください。');
      this.isSaving.set(false);
      return;
    }

    const fields = formValue.entityFields.map(
      (f: {
        fieldName: string;
        length: number;
        trimType?: string;
        fieldType?: string;
        count?: number | string | null;
        format?: string;
        textAlignment?: string;
      }) => {
        const field: Record<string, unknown> = { fieldName: f.fieldName, length: Number(f.length) };
        if (f.trimType) field['trimType'] = f.trimType;
        if (f.fieldType) field['fieldType'] = f.fieldType;
        const countRaw = f.count;
        const countNum =
          countRaw != null && countRaw !== ''
            ? Number(countRaw)
            : NaN;
        if (Number.isFinite(countNum) && countNum >= 1) {
          field['count'] = countNum;
        }
        const fmt = typeof f.format === 'string' ? f.format.trim() : '';
        if (fmt) field['format'] = fmt;
        const align = typeof f.textAlignment === 'string' ? f.textAlignment.trim() : '';
        if (align) field['textAlignment'] = align;
        return field;
      },
    );

    const payload: JsonToFixedTaskConfig = {
      id: formValue.id,
      name: formValue.name,
      wbsType: 'task',
      profiles: {
        actions: {
          actionType: 'execute',
          converter: {
            target: {
              searchPath: formValue.searchPath,
            },
            convertSchema: {
              contentsType: 'fixed',
              charset: 'shift_jis',
              fields: fields as FixedEntityField[],
            },
            saveTarget: {
              savePath: formValue.savePath,
            },
          },
        },
      },
    };

    const conv = payload.profiles.actions.converter;
    if (viewSchema) conv.viewSchema = viewSchema as NonNullable<typeof conv.viewSchema>;
    if (crossJoinOptions) conv.crossJoinOptions = crossJoinOptions as NonNullable<typeof conv.crossJoinOptions>;
    if (saveModel) conv.saveTarget.saveModel = saveModel as NonNullable<typeof conv.saveTarget.saveModel>;

    this.taskService.postTaskFile(payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.toastService.success('設定を保存しました');
      },
      error: (err) => {
        this.isSaving.set(false);
        this.error.set('設定の保存に失敗しました');
        console.error('Save config error:', err);
      },
    });
  }

  executeTask(): void {
    this.isExecuting.set(true);
    this.error.set(null);
    this.hasExecuted.set(true);
    this.isLoadingResult.set(true);
    this.resultText.set('');

    const formValue = this.configForm.getRawValue();
    const wbsTaskId = formValue.wbsTaskId || formValue.id;

    this.taskService.runConvertTask(wbsTaskId, this.BUCKET_CODE).subscribe({
      next: (response) => {
        if (response.resultCode === 0) {
          const savePath = formValue.savePath;
          this.fetchFixedText(savePath);
        } else {
          this.error.set(response.resultMessage || 'タスクの実行に失敗しました。');
          this.isExecuting.set(false);
          this.isLoadingResult.set(false);
        }
      },
      error: (err) => {
        this.error.set('タスクの実行に失敗しました。接続を確認してください。');
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
        console.error('Task execution error:', err);
      },
    });
  }

  /** 変換結果は固定長テキストのため JSON パースしない */
  private fetchFixedText(savePath: string): void {
    // 変換結果は Shift_JIS 固定長の想定（convertSchema.charset と整合）
    this.taskService.getRawFileContentDecoded(savePath, 'shift_jis').subscribe({
      next: (text) => {
        this.resultText.set(text);
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
      },
      error: (err) => {
        this.error.set(`変換結果ファイル (${savePath}) の取得に失敗しました。`);
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
        console.error('File fetch error:', err);
      },
    });
  }
}
